// Alle beløp lagres som heltall i øre, aldri som desimaltall. Det unngår
// avrundingsfeil når stjerner ganges opp og saldoer summeres.
//
// Tekstene her settes sammen av tall og ord, og må derfor bygges av t() med
// plassholdere framfor å limes sammen først og oversettes etterpå: ordbøkene
// bygges av `npm run i18n`, som leser literalene i koden. «3 ganger per dag»
// finnes ikke som literal noe sted, og ville aldri fått en oversettelse.
import { t } from './i18n'

export function formatMoney(ore, currency = 'kr') {
  const value = (Number(ore) || 0) / 100
  const text = new Intl.NumberFormat('nb-NO', {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(value)
  return `${text} ${currency}`
}

export function formatSigned(ore, currency = 'kr') {
  const sign = Number(ore) > 0 ? '+' : ''
  return sign + formatMoney(ore, currency)
}

/** "12,50" eller "12" -> 1250 øre. Godtar både komma og punktum. */
export function parseAmountToOre(input) {
  if (input === null || input === undefined) return null
  const cleaned = String(input).replace(/\s/g, '').replace(',', '.')
  if (cleaned === '') return null
  const value = Number(cleaned)
  if (!Number.isFinite(value)) return null
  return Math.round(value * 100)
}

/** Øre -> tekst egnet for et input-felt ("1250" -> "12,50"). */
export function oreToInput(ore) {
  const value = (Number(ore) || 0) / 100
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace('.', ',')
}

const dateFmt = new Intl.DateTimeFormat('nb-NO', { day: 'numeric', month: 'short' })
const timeFmt = new Intl.DateTimeFormat('nb-NO', { hour: '2-digit', minute: '2-digit' })

/**
 * En dato uten klokkeslett, slik den skrives i hint og lister: «12. aug».
 *
 * Rene datoer ('2026-08-12') leses som lokal tid og ikke som UTC. new Date()
 * tolker dem som midnatt UTC, og i en tidssone bak UTC ville datoen vist seg
 * som dagen før - en utbetaling som står oppført på feil dag.
 */
export function formatDate(value) {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-').map(Number)
    return dateFmt.format(new Date(year, month - 1, day))
  }
  return dateFmt.format(value instanceof Date ? value : new Date(value))
}

export function formatWhen(iso) {
  const date = new Date(iso)
  const now = new Date()
  const sameDay = date.toDateString() === now.toDateString()
  if (sameDay) return t('I dag {time}', { time: timeFmt.format(date) })

  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (date.toDateString() === yesterday.toDateString()) {
    return t('I går {time}', { time: timeFmt.format(date) })
  }

  return `${dateFmt.format(date)} ${timeFmt.format(date)}`
}

export function formatRelative(iso) {
  const diffMin = Math.round((Date.now() - new Date(iso).getTime()) / 60000)
  if (diffMin < 1) return t('nå nettopp')
  if (diffMin < 60) return t('for {count} min siden', { count: diffMin })
  const diffHours = Math.round(diffMin / 60)
  if (diffHours < 24) return t('for {count} t siden', { count: diffHours })
  return formatWhen(iso)
}

/**
 * Hvor ofte oppgaven kan gjøres. Dag og uke skrives ut hver for seg, i stedet
 * for å settes sammen av «per» + enhet: mange språk bøyer ordet etter
 * sammenhengen, og en tekst limt sammen av biter kan ikke oversettes godt.
 */
export function limitText(task) {
  if (task.limit_period === 'none') return t('Så ofte man vil')
  if (task.limit_period === 'day') {
    return task.limit_count === 1
      ? t('1 gang per dag')
      : t('{count} ganger per dag', { count: task.limit_count })
  }
  return task.limit_count === 1
    ? t('1 gang per uke')
    : t('{count} ganger per uke', { count: task.limit_count })
}

/**
 * Hvor ofte oppgaven gir penger, når det er sjeldnere enn den kan gjøres.
 * null betyr at hver gang betales - da er det ingenting å si fra om.
 */
export function payLimitText(task) {
  if (!task.pay_limit_period || task.pay_limit_period === 'none') return null
  if (task.pay_limit_period === 'day') {
    return task.pay_limit_count === 1
      ? t('penger 1 gang per dag')
      : t('penger {count} ganger per dag', { count: task.pay_limit_count })
  }
  return task.pay_limit_count === 1
    ? t('penger 1 gang per uke')
    : t('penger {count} ganger per uke', { count: task.pay_limit_count })
}

/**
 * Starten på inneværende dag eller uke, i lokal tid. Speiler date_trunc i
 * databasen, slik at appen kan vise samme regnestykke som grensene faktisk
 * håndheves med.
 */
export function periodStart(period) {
  const start = new Date()
  start.setHours(0, 0, 0, 0)
  if (period === 'week') start.setDate(start.getDate() - ((start.getDay() + 6) % 7))
  return start
}

/**
 * Verdien av en oppgave i øre. amount_ore er et fast beløp når det er satt -
 * da bestemmer det alene hva oppgaven gir, uavhengig av stjernene. Er det
 * null, følger oppgaven familiens stjernekurs.
 */
export function taskValueOre(task, starValueOre) {
  if (task.amount_ore !== null && task.amount_ore !== undefined) return Number(task.amount_ore)
  return (task.stars || 0) * starValueOre
}
